import { Box, BoxProps, Flex, FlexProps, useDisclosure } from '@chakra-ui/react';
import { signOut } from 'firebase/auth';
import { auth } from '~/firebase/init';

import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';
import CustomerInfoEditDialog from '~/components/CustomerInfoEditDialog';
import Footer from '~/components/Footer';
import Header from '~/components/Header';
import LocationSelectorModal from '~/components/LocationSelectorModal';
import LoginModal from '~/components/LoginModal';
import NavBar from '~/components/NavBar';
import NavDrawer from '~/components/NavDrawer';
import PickupSettingModal from '~/components/PickupSettingModal';
import authAtom from '~/recoil/atom/auth';
import customerAtom from '~/recoil/atom/customer';
import modalControlAtom from '~/recoil/atom/modalControl';
import Content from './Content';

type PageContainerProps = {
  children?: React.ReactNode;
  containerStyles?: FlexProps;
  topBoxStyle?: BoxProps;
  hideHeader?: boolean;
  hideNavBar?: boolean;
  hideFooter?: boolean;
  afterHeaderComponent?: React.ReactNode;
};

export default function PageContainer({
  children,
  containerStyles = {},
  topBoxStyle = {},
  hideFooter = false,
  hideHeader = false,
  hideNavBar = false,
  afterHeaderComponent = null,
}: PageContainerProps) {
  const [
    { isLoginModalOpen, isPickMealPointModalOpen, isPickupSettingModalOPen },
    setModalControl,
  ] = useRecoilState(modalControlAtom);
  const authUser = useRecoilValue(authAtom);
  const customer = useRecoilValue(customerAtom);
  const { enterpriseGroupBuyId = '' } = useParams();
  const {
    isOpen: isLoginFormOpen,
    onClose: onCloseLoginForm,
    onOpen: onOpenLoginForm,
  } = useDisclosure();

  const { isOpen, onClose, onOpen } = useDisclosure();

  const handleLoginLogout = () => {
    if (authUser) {
      signOut(auth);
      return;
    }

    setModalControl((prev) => ({ ...prev, isLoginModalOpen: true }));
    onClose();
  };

  const handleLoginModalClose = () => {
    setModalControl((prev) => ({ ...prev, isLoginModalOpen: false }));
  };

  const handlePickMealPointModalClose = () => {
    setModalControl((prev) => ({ ...prev, isPickMealPointModalOpen: false }));
  };

  const handlePickupSettingModalClose = () => {
    setModalControl((prev) => ({ ...prev, isPickupSettingModalOPen: false }));
  };

  useEffect(() => {
    if ((customer?.displayName || '')?.length > 0 && !customer?.isLoginFormSubmitted) {
      onOpenLoginForm();
    }
  }, [customer?.isLoginFormSubmitted]);

  return (
    <Flex direction="column" align="stretch" bg="#F7F7F7" minH="100vh" {...containerStyles}>
      <Box pos="sticky" top={0} zIndex={100} {...topBoxStyle}>
        {!hideHeader && (
          <Box as="header" bg="white" px={4} borderBottomColor="primary" borderBottomWidth="1px">
            <Content m="auto">
              <Header onOpen={onOpen} />
            </Content>
          </Box>
        )}

        {afterHeaderComponent}

        <NavBar
          isOpen={isOpen}
          isLogin={!!authUser}
          onClose={onClose}
          onLoginLogout={handleLoginLogout}
          display={{ base: 'none', md: !hideNavBar ? 'block' : 'none' }}
        />
      </Box>

      <Box as="main" flex="1" bg="white">
        {children}
      </Box>

      {!hideFooter && (
        <Box px={4} bgColor="gray.100">
          <Content as="footer" m="auto" py={4}>
            <Footer />
          </Content>
        </Box>
      )}
      <NavDrawer
        isOpen={isOpen}
        isLogin={!!authUser}
        onClose={onClose}
        onLoginLogout={handleLoginLogout}
      />

      <>
        {!enterpriseGroupBuyId ? (
          <LoginModal isOpen={isLoginModalOpen} onClose={handleLoginModalClose} />
        ) : null}

        {customer ? (
          <CustomerInfoEditDialog
            isOpen={isLoginFormOpen}
            onClose={onCloseLoginForm}
            allowedClose={!!customer?.isLoginFormSubmitted}
          />
        ) : null}

        <LocationSelectorModal
          isOpen={isPickMealPointModalOpen}
          onClose={handlePickMealPointModalClose}
        />
        <PickupSettingModal
          isOpen={isPickupSettingModalOPen}
          onClose={handlePickupSettingModalClose}
        />
      </>
    </Flex>
  );
}
